import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getRentPayments, getRentSummary, payRent, getTenantLeases } from '@/services/api';
import { 
  IndianRupee, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  Home, 
  CreditCard,
  AlertCircle
} from 'lucide-react';

const TenantRent = () => {
  const { user } = useAuth();
  const [rentPayments, setRentPayments] = useState([]);
  const [rentSummary, setRentSummary] = useState(null);
  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [paymentsData, summaryData, leasesData] = await Promise.all([
        getRentPayments(),
        getRentSummary(),
        getTenantLeases()
      ]);
      setRentPayments(paymentsData);
      setRentSummary(summaryData);
      if (leasesData && leasesData.length > 0) {
        setLease(leasesData[0]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePayRent = async (paymentId) => {
    try {
      setPayingId(paymentId);
      await payRent(paymentId);
      toast.success('Payment submitted! Waiting for landlord confirmation.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setPayingId(null);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const pendingPayments = rentPayments.filter(p => p.status === 'pending' || p.status === 'unpaid');
  const submittedPayments = rentPayments.filter(p => p.status === 'submitted');
  const paidPayments = rentPayments.filter(p => p.status === 'paid');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-rent">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Rent Payments</h1>
        <p className="text-slate-600 mt-1">Manage your monthly rent payments</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={pendingPayments.length > 0 ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${pendingPayments.length > 0 ? 'text-red-600' : 'text-green-600'}`}>Pending Dues</p>
                <p className={`text-2xl font-bold flex items-center gap-1 ${pendingPayments.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  <IndianRupee className="h-5 w-5" />
                  {rentSummary?.total_due?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              {pendingPayments.length > 0 ? (
                <AlertCircle className="h-8 w-8 text-red-400" />
              ) : (
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Awaiting Confirmation</p>
                <p className="text-2xl font-bold text-amber-700">{submittedPayments.length}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Total Paid</p>
                <p className="text-2xl font-bold text-green-700 flex items-center gap-1">
                  <IndianRupee className="h-5 w-5" />
                  {rentSummary?.total_paid?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Monthly Rent</p>
                <p className="text-2xl font-bold text-blue-700 flex items-center gap-1">
                  <IndianRupee className="h-5 w-5" />
                  {lease?.monthly_rent?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lease Info */}
      {lease && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="h-5 w-5 text-secondary" />
              Lease Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Unit Number</p>
                <p className="font-semibold text-slate-900">{lease.unit_number}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Landlord</p>
                <p className="font-semibold text-slate-900">{lease.landlord_name}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Lease Start</p>
                <p className="font-semibold text-slate-900">{lease.lease_start}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Status</p>
                <Badge className="bg-green-100 text-green-700">{lease.status}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Payments */}
      {pendingPayments.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="bg-amber-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Clock className="h-5 w-5" />
              Pending Rent Payments
            </CardTitle>
            <CardDescription className="text-amber-600">
              Pay your pending rent to avoid penalties
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {pendingPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200"
                  data-testid={`pending-payment-${payment.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-amber-100 rounded-lg">
                      <Clock className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{formatMonth(payment.month)}</p>
                      <p className="text-sm text-slate-500">Unit: {payment.unit_number} | To: {payment.landlord_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-slate-900 flex items-center gap-1">
                        <IndianRupee className="h-4 w-4" />
                        {payment.amount?.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <Button
                      onClick={() => handlePayRent(payment.id)}
                      disabled={payingId === payment.id}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`pay-btn-${payment.id}`}
                    >
                      {payingId === payment.id ? 'Processing...' : 'Submit Payment'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submitted Payments (Awaiting Confirmation) */}
      {submittedPayments.length > 0 && (
        <Card className="border-blue-200">
          <CardHeader className="bg-blue-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Clock className="h-5 w-5" />
              Awaiting Landlord Confirmation
            </CardTitle>
            <CardDescription className="text-blue-600">
              These payments are awaiting confirmation from your landlord
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {submittedPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{formatMonth(payment.month)}</p>
                      <p className="text-sm text-slate-500">Submitted: {payment.submitted_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-bold text-slate-900 flex items-center gap-1">
                      <IndianRupee className="h-4 w-4" />
                      {payment.amount?.toLocaleString('en-IN')}
                    </p>
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200">Awaiting Confirmation</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-secondary" />
            Payment History
          </CardTitle>
          <CardDescription>Your completed rent payments</CardDescription>
        </CardHeader>
        <CardContent>
          {paidPayments.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CreditCard className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p>No completed payments yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paidPayments.map((payment) => (
                <div key={payment.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`paid-payment-${payment.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{formatMonth(payment.month)}</p>
                        <p className="text-xs text-slate-500">
                          {payment.confirmed_by_name
                            ? `Confirmed by ${payment.confirmed_by_name} on ${payment.confirmed_date}`
                            : payment.recorded_by_name
                            ? `Recorded by ${payment.recorded_by_name} on ${payment.paid_date}`
                            : `Paid: ${payment.confirmed_date || payment.paid_date}`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 flex items-center gap-1">
                        <IndianRupee className="h-3 w-3" />
                        {payment.amount?.toLocaleString('en-IN')}
                      </span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Paid</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantRent;
